import { useMemo, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useRevitApi } from '../../../shared/api/revit/useRevitApi';
import {
  areProjectParametersAvailableForBinding,
  collectConnectorBindingParameters,
  readAvailableProjectParameterCache,
  useParameterAddition,
} from '../../../shared/lib';
import {
  buildAdditionalCategoryAdditionOperations,
  buildConnectorAdditionOperations,
} from '../../../shared/lib/hooks/useParameterAddition';
import { normalizeMappingProjectKey } from '../../../shared/lib/mappingProjectIdentity';
import { describeUndoPlanAffordance } from '../../../shared/lib/parameterUndo/undoAffordance';
import { toBoltConfigurationInput } from '../../../shared/lib/utils/boltConfigurationInput';
import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import type { QuickFillRowMode } from '../../../shared/types';
import { extractBoltParameterNames } from '../../../shared/services/boltParameterSetup';
import {
  buildQuickFillParameterBindingKinds,
  resolveQuickFillConnectorDefaultRowMode,
  resolveQuickFillSnapshotShape,
} from '../lib/quickFillPageModel';
import {
  buildMappedQuickFillParameterIntentGroups,
  buildQuickFillRequestedParameterIntents,
} from '../lib/quickFillParameterIntentPlanner';
import { collectMappedQuickFillColumns } from './useQuickFillConfigSync';

export function useQuickFillPageConnectorController(
  footerActionsRef?: React.RefObject<HTMLDivElement | null>
) {
  const theme = useTheme();
  const isNarrowWidth = useMediaQuery(theme.breakpoints.down(1100));
  const isShortViewport = useMediaQuery('(max-height:560px)');
  const parameterAddition = useParameterAddition();
  const revitApi = useRevitApi();
  const menuConfiguration = useConfigurationStore((state) => state.menuConfiguration);
  const connectorBindingConfiguration = useConfigurationStore(
    (state) => state.connectorBindingConfiguration
  );
  const boltConfiguration = useConfigurationStore((state) => state.boltConfiguration);
  const additionalCategoriesConfiguration = useConfigurationStore(
    (state) => state.additionalCategoriesConfiguration
  );
  const mappingConfiguration = useConfigurationStore(
    (state) => state.mappingConfiguration
  );
  const activeDocumentInfo = useActiveDocumentInfoContext();
  const connectorQuickFillTarget = useMemo(() => ({
    parameterDestination: connectorBindingConfiguration.parameterDestination,
    parameterBindingKind: connectorBindingConfiguration.parameterBindingKind,
  }), [connectorBindingConfiguration]);
  const projectParameterCacheKey = useMemo(
    () => normalizeMappingProjectKey(activeDocumentInfo.projectKey),
    [activeDocumentInfo.projectKey]
  );
  const quickFillCacheScopeKey = useMemo(() => {
    const projectKey = normalizeMappingProjectKey(activeDocumentInfo.projectKey);
    const version = Number.isFinite(activeDocumentInfo.modelVersion)
      ? activeDocumentInfo.modelVersion
      : 'unknown';
    return `${projectKey || 'unknown-document'}::v${version}`;
  }, [activeDocumentInfo.modelVersion, activeDocumentInfo.projectKey]);
  const connectorParametersWereCreatedAsProjectInstance = useMemo(() => {
    if (connectorBindingConfiguration.parameterDestination !== 'project') return false;
    const cachedParameters = readAvailableProjectParameterCache(projectParameterCacheKey);
    if (!cachedParameters?.length) return false;
    const names = collectConnectorBindingParameters({
      menuConfig: menuConfiguration,
      connectorConfig: connectorBindingConfiguration,
    });
    return names.length > 0 && areProjectParametersAvailableForBinding(
      names,
      'instance',
      cachedParameters
    );
  }, [connectorBindingConfiguration, menuConfiguration, projectParameterCacheKey]);
  const quickFillConnectorTarget = useMemo(
    () => connectorParametersWereCreatedAsProjectInstance
      ? { parameterDestination: 'project' as const, parameterBindingKind: 'instance' as const }
      : connectorQuickFillTarget,
    [connectorParametersWereCreatedAsProjectInstance, connectorQuickFillTarget]
  );
  const connectorQuickFillOperations = useMemo(
    () => buildConnectorAdditionOperations({
      menuConfig: menuConfiguration,
      connectorConfig: {
        ...connectorBindingConfiguration,
        ...quickFillConnectorTarget,
      },
    }),
    [connectorBindingConfiguration, menuConfiguration, quickFillConnectorTarget]
  );
  const mappedProjectParameterIntentGroups = useMemo(
    () => buildMappedQuickFillParameterIntentGroups(
      collectMappedQuickFillColumns(mappingConfiguration, [], [])
        .map((property) => property.revitParameter)
        .filter((name): name is string => Boolean(name)),
      quickFillConnectorTarget.parameterBindingKind
    ),
    [mappingConfiguration, quickFillConnectorTarget.parameterBindingKind]
  );
  const connectorDefaultRowMode = useMemo(
    () => resolveQuickFillConnectorDefaultRowMode(quickFillConnectorTarget),
    [quickFillConnectorTarget]
  );
  const quickFillParameterBindingKinds = useMemo(
    () => buildQuickFillParameterBindingKinds([
      ...connectorQuickFillOperations.map((operation) => ({
        parameterNames: operation.parameterNames,
        bindingKind: operation.bindingKind,
      })),
      {
        parameterNames: extractBoltParameterNames(toBoltConfigurationInput(
          boltConfiguration,
          {
            parameterName: menuConfiguration.objectTypeValue,
            bindingKind: connectorBindingConfiguration.parameterBindingKind,
          }
        )),
        bindingKind: boltConfiguration.parameterBindingKind,
      },
      ...buildAdditionalCategoryAdditionOperations(additionalCategoriesConfiguration).map(
        (operation) => ({
          parameterNames: operation.parameterNames,
          bindingKind: operation.bindingKind,
        })
      ),
      ...mappedProjectParameterIntentGroups,
    ]),
    [
      additionalCategoriesConfiguration,
      boltConfiguration,
      connectorBindingConfiguration,
      connectorQuickFillOperations,
      mappedProjectParameterIntentGroups,
      menuConfiguration,
    ]
  );
  const quickFillRequestedParameterIntents = useMemo(
    () => buildQuickFillRequestedParameterIntents([
      ...connectorQuickFillOperations.map((operation) => ({
        parameterNames: operation.parameterNames,
        destination: operation.destination,
        bindingKind: operation.bindingKind,
      })),
      {
        parameterNames: extractBoltParameterNames(toBoltConfigurationInput(
          boltConfiguration,
          {
            parameterName: menuConfiguration.objectTypeValue,
            bindingKind: connectorBindingConfiguration.parameterBindingKind,
          }
        )),
        destination: boltConfiguration.parameterDestination,
        bindingKind: boltConfiguration.parameterBindingKind,
      },
      ...buildAdditionalCategoryAdditionOperations(additionalCategoriesConfiguration).map(
        (operation) => ({
          parameterNames: operation.parameterNames,
          destination: operation.destination,
          bindingKind: operation.bindingKind,
        })
      ),
      ...mappedProjectParameterIntentGroups,
    ]),
    [
      additionalCategoriesConfiguration,
      boltConfiguration,
      connectorBindingConfiguration,
      connectorQuickFillOperations,
      mappedProjectParameterIntentGroups,
      menuConfiguration,
    ]
  );
  const [rowMode, setRowMode] = useState<QuickFillRowMode>(() => connectorDefaultRowMode);
  const manualRowModeOverrideRef = useRef(false);
  const consumedValidationFocusRequestIdRef = useRef<string | null>(null);
  const lastValidationFocusWaitStateRef = useRef<string | null>(null);
  const quickFillSnapshotShape = useMemo(
    () => resolveQuickFillSnapshotShape(quickFillConnectorTarget, rowMode),
    [quickFillConnectorTarget, rowMode]
  );
  const refs = {
    actionBarRef: useRef<HTMLDivElement | null>(null),
    pageGuideClusterRef: useRef<HTMLDivElement | null>(null),
    recoveryControlsRef: useRef<HTMLDivElement | null>(null),
    pageGuideRef: useRef<HTMLSpanElement | null>(null),
    autoFillControlsRef: useRef<HTMLDivElement | null>(null),
    manageColumnsRef: useRef<HTMLButtonElement | null>(null),
    findReplaceRef: useRef<HTMLButtonElement | null>(null),
    rowModeToggleRef: useRef<HTMLLabelElement | null>(null),
    modelSelectRef: useRef<HTMLButtonElement | null>(null),
    csvToolsRef: useRef<HTMLDivElement | null>(null),
    utilityToolsRef: useRef<HTMLDivElement | null>(null),
    tableAreaRef: useRef<HTMLDivElement | null>(null),
    tableViewportRef: useRef<HTMLDivElement | null>(null),
    navigationDockRef: useRef<HTMLDivElement | null>(null),
    filterTriggerRef: useRef<HTMLElement | null>(null),
    filterPopoverRef: useRef<HTMLDivElement | null>(null),
    propertyPanelRef: useRef<HTMLDivElement | null>(null),
    propertySearchRef: useRef<HTMLInputElement | null>(null),
    valuePanelRef: useRef<HTMLDivElement | null>(null),
    valueSearchRef: useRef<HTMLInputElement | null>(null),
    autoFillPreviewDialogRef: useRef<HTMLDivElement | null>(null),
    findReplaceDialogRef: useRef<HTMLDivElement | null>(null),
    saveReviewDialogRef: useRef<HTMLDivElement | null>(null),
    localFooterActionsRef: useRef<HTMLDivElement | null>(null),
    propertyPanelOpenBeforeTourRef: useRef(false),
    propertyPanelManagedByTourRef: useRef(false),
  };

  return {
    useDrawerPanels: isNarrowWidth || isShortViewport,
    isShortViewport,
    parameterAddition,
    revitApi,
    menuConfiguration,
    connectorBindingConfiguration,
    activeDocumentInfo,
    connectorDefaultRowMode,
    quickFillParameterBindingKinds,
    quickFillRequestedParameterIntents,
    quickFillCacheScopeKey,
    quickFillSnapshotShape,
    rowMode,
    setRowMode,
    manualRowModeOverrideRef,
    consumedValidationFocusRequestIdRef,
    lastValidationFocusWaitStateRef,
    refs,
    tourFooterActionsRef: footerActionsRef ?? refs.localFooterActionsRef,
    connectorUndoAffordance: describeUndoPlanAffordance(parameterAddition.lastConnectorUndoPlan),
  };
}

export type QuickFillPageConnectorController = ReturnType<
  typeof useQuickFillPageConnectorController
>;
